import { isRedirectDisabled } from "@/apiUtils/commonUtils";
import { sessionFlowConstants, sessionQueryParams } from "@/apiUtils/constants";
import { HeaderContent, SocialMediaIcon } from "@/common/lib/types";
import { CLink } from "@/components/common/customLink/CustomLink";
import LocaleSelector from "@/components/localeSelector/LocaleSelector";
import SignInSignUpModal from "@/components/MagrabiComponents/SignInSignUpModal";
import Button from "@/components/ui/button/Button";
import Icon from "@/components/ui/icon/Icon";
import { closeIcon } from "@/constants/icons";
import useCRouter from "@/hooks/useCRouter";
import useSearchParams from "@/hooks/useSearchParams";
import { useNavContext } from "@/providers/NavProvider";
import { useGlobalStore } from "@/store/store";
import { getDirFromLocale, getLangFromLocale } from "@/utils/common";
import { useParams } from "next/navigation";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ProfileIcon from "../ProfileIcon";
import { NavAccordianV2 } from "./NavAccordianV2";

interface IProps {
  open?: boolean;
  onClose?: () => void;
  headerContent: HeaderContent;
  socialMediaIcons: SocialMediaIcon[];
  translation: Record<string, string>;
}

const NavMobileMenu = ({ onClose, open, headerContent, socialMediaIcons, translation }: IProps) => {
  // hooks
  const router = useCRouter();
  const { locale } = useParams();
  const { updateSearchParam, searchParams } = useSearchParams();

  const [isClient, setIsClient] = useState<boolean>(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [loadedOnce, setLoadedOnce] = useState<boolean>(false);
  const [isLoginModalLoaded, setIsLoginModalLoaded] = useState<boolean | undefined>(false);

  const { navData } = useNavContext();
  const user = useGlobalStore((state) => state.user);
  const isAuthenticated = useGlobalStore((store) => store.isAuthenticated);

  // consts
  const lang = getLangFromLocale(locale as string);
  const dir = getDirFromLocale(locale as string);
  const headers = navData?.headerModules ?? [];
  const menu = navData?.menu?.menu ?? [];

  const { sessionInfoM, sessionInfoTabs, isCheckout } = useMemo(() => {
    const utmSource = searchParams?.get("utm_source");
    const isCartAbandon = utmSource === sessionFlowConstants.ABANDON_EMAIL;

    return {
      sessionInfoM: searchParams.get(sessionQueryParams.LOGIN_MODAL_STATUS),
      sessionInfoTabs: isCartAbandon
        ? [sessionFlowConstants.LOGIN_EMAIL, sessionFlowConstants.LOGIN_PHONE]
        : [
          sessionFlowConstants.LOGIN_EMAIL,
          sessionFlowConstants.LOGIN_PHONE,
          sessionFlowConstants.REGISTER,
          sessionFlowConstants.GUEST,
        ],
      isCheckout: searchParams.get(sessionQueryParams.IS_CHECKOUT),
    };
  }, [searchParams]);

  const [activeTab, setActiveTab] = useState<string>(sessionInfoM || sessionInfoTabs[0]);

  const show = useMemo(() => {
    if (sessionInfoM) {
      setActiveTab(sessionInfoM);
      return sessionInfoTabs.includes(sessionInfoM);
    }
    return false;
  }, [sessionInfoM, sessionInfoTabs]);

  const handleSignInOrSignUp = (tab?: string) => {
    if (isAuthenticated) {
      router.push("/my-account/dashboard");
      return;
    }

    setIsLoginModalLoaded(true);
    updateSearchParam([
      { key: sessionQueryParams.LOGIN_MODAL_STATUS, value: activeTab || tab || sessionInfoTabs[0] },
    ]);

    onClose?.();
  };

  const handleOnLoaded = () => {
    setIsLoginModalLoaded(false);
  };

  const handleCloseAuthDrawer = () => {
    setActiveTab(sessionInfoM || sessionInfoTabs[0]);
    setIsLoginModalLoaded(false);
    updateSearchParam([
      { key: sessionQueryParams.LOGIN_MODAL_STATUS, value: undefined },
      { key: sessionQueryParams.IS_CHECKOUT, value: undefined },
      { key: "utm_source", value: undefined },
      { key: "source", value: undefined },
    ], true);
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setLoadedOnce(true);
    }, 2000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed left-0 top-0 z-30 h-[100dvh] w-full bg-black/60 ${open ? "block" : "hidden"}`}
      ></div>

      <div
        className={
          "fixed start-0 top-0 z-50 flex h-full w-full flex-col bg-white transition-transform duration-300 ease-in-out"
        }
        style={{
          transform: open
            ? "translateX(0px)"
            : dir === "ltr"
              ? "translateX(-100%)"
              : "translateX(100%)",
        }}
      >
        <div className="bg-white pt-3">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center px-5"
            title="close menu"
          >
            <Icon src={closeIcon} title="close" size={14} />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-4 bg-white px-5 py-7">
          {isAuthenticated ? (
            <div className="flex-1">
              <Suspense fallback={<></>}>
                <ProfileIcon
                  usePrimaryColor={false}
                  primaryTextColor="text-black"
                  enableAppleLogin={headerContent?.enableAppleLogin ?? false}
                  enableFacebookLogin={headerContent?.enableFacebookLogin ?? false}
                  enableGoogleLogin={headerContent?.enableGoogleLogin ?? false}
                  onClick={onClose}
                />
              </Suspense>
            </div>
          ) : (
            <Button
              variant="outlined"
              onClick={() => handleSignInOrSignUp(activeTab)}
              className="min-w-min flex-1 whitespace-nowrap p-2"
            >
              {translation?.header_signup_button_label}
            </Button>
          )}
          <Button
            className="min-w-min flex-1 whitespace-nowrap p-2"
            onClick={() => {
              if (!user) handleSignInOrSignUp(activeTab);
              else {
                router.push("/my-account/orders?filter=reorder");
                onClose?.();
              }
            }}
          >
            {translation?.header_button_reorder_cl}
          </Button>
        </div>

        <div className="flex h-full flex-col overflow-auto">
          <div className="px-5">
            <NavAccordianV2 menu={menu} closeMenu={onClose} />
          </div>

          <div className="mt-auto flex flex-col gap-8 bg-tertiary-5 px-5 pb-11 pt-5 text-black">
            <div className="flex flex-wrap items-center gap-4">
              {headers?.map((header, idx) => (
                <CLink
                  key={idx}
                  href={header.link?.url ?? "/"}
                  onClick={onClose}
                  className="text-xs font-bold underline"
                >
                  {lang === "en" ? header.title.en : header.title.ar}
                </CLink>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <LocaleSelector />
            </div>
            <div className="mt-auto flex flex-wrap items-center justify-around gap-6">
              {socialMediaIcons.map((v) => (
                <CLink key={v.key} href={v.url}>
                  <Icon src={v.media} title={v.type} size={20} />
                </CLink>
              ))}
            </div>
          </div>
        </div>
      </div>
      {loadedOnce && !isAuthenticated && isClient
        ? createPortal(
            <SignInSignUpModal
              show={!!show}
              translations={{}}
              activeTab={activeTab}
              tabs={sessionInfoTabs}
              onLoaded={handleOnLoaded}
              isCheckoutFlow={!!isCheckout}
              onClose={handleCloseAuthDrawer}
              disableRedirect={isRedirectDisabled(searchParams)}
            />,
            document.body,
            "key-session",
          )
        : null}
    </>
  );
};

export default NavMobileMenu;
